import React from "react";
import { motion } from "framer-motion";
import {
  Send,
  ShieldCheck,
  Settings2,
  MessageSquare,
  CheckCircle2,
  RotateCcw,
} from "lucide-react";

const processSteps = [
  {
    number: "01",
    icon: Send,
    title: "API Request",
    description:
      "Your application sends a request to the API with the required authentication, recipient, message, or verification details.",
  },
  {
    number: "02",
    icon: ShieldCheck,
    title: "Authentication & Validation",
    description:
      "The request is authenticated and validated to ensure the API credentials, parameters, and required information are correct.",
  },
  {
    number: "03",
    icon: Settings2,
    title: "Request Processing",
    description:
      "Our API infrastructure processes the request and routes it through the appropriate communication service based on your application workflow.",
  },
  {
    number: "04",
    icon: MessageSquare,
    title: "Message Delivery",
    description:
      "The approved request is passed to the selected communication channel, such as WhatsApp messaging or SMS OTP delivery.",
  },
  {
    number: "05",
    icon: CheckCircle2,
    title: "Delivery Status",
    description:
      "Your application can receive the relevant processing or delivery information and use it to continue the next step in your workflow.",
  },
  {
    number: "06",
    icon: RotateCcw,
    title: "API Response",
    description:
      "A structured API response is returned to your application so your system can handle success, failure, or other required outcomes.",
  },
];

const ProcessSection = () => {
  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        py-12
        sm:py-14
        md:py-16
        lg:py-20
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-24
          h-56
          w-56
          rounded-full
          bg-orange-500/[0.05]
          blur-[100px]
          sm:h-72
          sm:w-72
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-20
          h-64
          w-64
          rounded-full
          bg-orange-500/[0.04]
          blur-[110px]
          sm:h-80
          sm:w-80
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* =================================================
            HEADING
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 25,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-9
            max-w-3xl
            text-center
            sm:mb-11
            lg:mb-12
          "
        >
          {/* Label */}

          <span
            className="
              inline-flex
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]
              px-3
              py-1.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[2px]
              text-[var(--brand-light)]
              sm:px-4
              sm:py-2
              sm:text-xs
              sm:tracking-[2.5px]
            "
          >
            How It Works
          </span>

          {/* Heading */}

          <h2
            className="
              mt-4
              text-2xl
              font-bold
              leading-tight
              tracking-tight
              text-[var(--text-primary)]
              sm:mt-5
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
          >
            From API Request{" "}
            <span className="text-[var(--brand-primary)]">
              To Successful Delivery
            </span>
          </h2>

          {/* Description */}

          <p
            className="
              mx-auto
              mt-3
              max-w-2xl
              text-xs
              leading-5
              text-[var(--text-muted)]
              sm:mt-4
              sm:text-sm
              sm:leading-6
              md:text-base
              md:leading-7
            "
          >
            Our API workflow is designed to keep communication simple for
            your application while the underlying infrastructure handles
            authentication, processing, routing, and delivery.
          </p>
        </motion.div>

        {/* =================================================
            TIMELINE
        ================================================== */}

        <div className="relative">
          {/* Desktop Timeline Line */}

          <div
            className="
              absolute
              left-7
              top-7
              hidden
              h-[calc(100%-3.5rem)]
              w-[2px]
              bg-gradient-to-b
              from-orange-500
              via-orange-500/50
              to-transparent
              lg:block
            "
          />

          <div className="space-y-5 sm:space-y-6 lg:space-y-7">
            {processSteps.map((step, index) => {
              const Icon = step.icon;

              return (
                <motion.div
                  key={step.number}
                  initial={{
                    opacity: 0,
                    x: -35,
                  }}
                  whileInView={{
                    opacity: 1,
                    x: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.15,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.07,
                    ease: "easeOut",
                  }}
                  className="
                    relative
                    flex
                    flex-col
                    gap-3
                    lg:flex-row
                    lg:items-center
                    lg:gap-7
                  "
                >
                  {/* =================================================
                      ICON
                  ================================================== */}

                  <motion.div
                    whileHover={{
                      scale: 1.06,
                    }}
                    transition={{
                      duration: 0.2,
                    }}
                    className="
                      relative
                      z-10
                      flex
                      h-12
                      w-12
                      shrink-0
                      items-center
                      justify-center
                      self-start
                      rounded-full
                      border
                      border-orange-500/30
                      bg-[var(--card-bg)]
                      text-[var(--brand-primary)]
                      shadow-[0_0_0_5px_var(--bg-primary)]
                      transition-all
                      duration-300
                      hover:border-orange-500/60
                      hover:bg-orange-500
                      hover:text-white
                      sm:h-14
                      sm:w-14
                      lg:h-14
                      lg:w-14
                    "
                  >
                    <Icon
                      size={22}
                      strokeWidth={1.8}
                      className="
                        transition-transform
                        duration-300
                      "
                    />
                  </motion.div>

                  {/* =================================================
                      CONTENT
                  ================================================== */}

                  <div
                    className="
                      group
                      flex-1
                      rounded-2xl
                      border
                      border-[var(--border-primary)]
                      bg-[var(--card-bg)]
                      px-5
                      py-5
                      transition-all
                      duration-300
                      hover:-translate-y-1
                      hover:border-orange-500/30
                      hover:bg-[var(--card-bg-secondary)]
                      sm:px-6
                      sm:py-6
                      lg:px-7
                      lg:py-6
                    "
                  >
                    {/* Step */}

                    <span
                      className="
                        text-[10px]
                        font-semibold
                        uppercase
                        tracking-[2px]
                        text-[var(--brand-light)]
                        sm:text-xs
                      "
                    >
                      Step {index + 1}
                    </span>

                    {/* Title */}

                    <h3
                      className="
                        mt-1.5
                        text-lg
                        font-bold
                        leading-snug
                        text-[var(--text-primary)]
                        transition-colors
                        duration-300
                        group-hover:text-[var(--brand-light)]
                        sm:text-xl
                      "
                    >
                      {step.title}
                    </h3>

                    {/* Description */}

                    <p
                      className="
                        mt-2
                        max-w-4xl
                        text-xs
                        leading-5
                        text-[var(--text-muted)]
                        transition-colors
                        duration-300
                        group-hover:text-[var(--text-secondary)]
                        sm:text-sm
                        sm:leading-6
                      "
                    >
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;